import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useSyncExternalStore } from 'react';
import { useAudio } from './AudioContext';
import { useInstrument } from './InstrumentContext';
import { API_URL } from '../config/api';
import { getSessionVersion, setSessionVersion } from '../utils/sessionVersion';
import { storageKeyFromUrl } from '../utils/storageKey';
import assetResolver from '../utils/AssetResolver';
import { isPreviewPlayer } from '../chat/isPreviewPlayer';
import {
  createPersistedDrumSample,
  drumSampleBufferKey,
  planCachedDrumSampleAssignment,
  resolveDrumSampleBuffer
} from '../utils/drumSamplePersistence';
import { decodeAudioFile } from '../audio/decodeAudioFile';
import { activateAudioContext } from '../audio/audioContextActivation';
import { recordUiWork, uiWorkStartedAt } from '../diagnostics/uiWorkProbe';
import {
  DRUM_VOICE_GAIN,
  applyDrumVoiceEnvelope,
  configureDrumBus,
  drumVoiceEnvelope,
  fadeAndStopDrumVoice,
  shouldChokeDrumLane
} from '../audio/drumSignalChain';
import { clearSchedulerInterval, setSchedulerInterval } from '../audio/schedulerClock';

const isDev = process.env.NODE_ENV !== 'production';
const devLog = (...args) => {
  if (isDev) console.log(...args);
};
const devWarn = (...args) => {
  if (isDev) console.warn(...args);
};

const DrumContext = createContext();
const DrumStepContext = createContext(null);

const createDrumStepStore = () => {
  let step = 0;
  const listeners = new Set();
  return {
    getSnapshot: () => step,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set: (nextStep) => {
      if (step === nextStep) return;
      step = nextStep;
      listeners.forEach((listener) => listener());
    }
  };
};

export const useDrum = () => {
  const context = useContext(DrumContext);
  if (!context) {
    throw new Error('useDrum must be used within a DrumProvider');
  }
  return context;
};

export const useDrumStep = () => {
  const store = useContext(DrumStepContext);
  if (!store) throw new Error('useDrumStep must be used within a DrumProvider');
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
};

const DEFAULT_DRUM_SOUNDS = [
  { name: 'Kick', key: 'kick', color: '#E91E63', playbackMode: 'drum' },
  { name: 'Snare', key: 'snare', color: '#2196F3', playbackMode: 'drum' },
  { name: 'Hi-Hat', key: 'hihat', color: '#FFC107', playbackMode: 'drum' },
  { name: 'Clap', key: 'clap', color: '#9C27B0', playbackMode: 'drum' },
  { name: 'Perc', key: 'perc', color: '#4CAF50', playbackMode: 'drum' }
];

const normalizePlaybackMode = (mode) => (mode === 'oneshot' ? 'oneshot' : 'drum');
const normalizeDrumSound = (sound = {}, fallback = {}) => ({
  ...fallback,
  ...sound,
  playbackMode: normalizePlaybackMode(sound?.playbackMode ?? fallback?.playbackMode)
});
const normalizeDrumSounds = (sounds = []) => (
  (Array.isArray(sounds) && sounds.length > 0 ? sounds : DEFAULT_DRUM_SOUNDS)
    .map((sound, idx) => normalizeDrumSound(sound, DEFAULT_DRUM_SOUNDS[idx] || { playbackMode: 'drum' }))
);
const clampValue = (value, min, max, fallback = min) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(min, Math.min(max, num));
};
const DRUM_CLICK_FADE_OUT = 0.008;

export const DrumProvider = ({ children }) => {
  const {
    audioContext,
    bpm,
    registerDrumVolumes,
    registerDrumBuffers,
    reschedulePatternsForDrumSampleChange,
    isRecording,
    masterGainRef
  } = useAudio();
  const { registerDrumTrigger } = useInstrument();
  const [drumBuffers, setDrumBuffers] = useState({});
  const [drumSounds, setDrumSounds] = useState(normalizeDrumSounds(DEFAULT_DRUM_SOUNDS));
  const [customSamples, setCustomSamples] = useState({}); // Track which drums have custom samples
  const [pattern, setPattern] = useState({});
  const [patternLength, setPatternLength] = useState(16);
  const [noteData, setNoteDataState] = useState({}); // Store pitch info for each step: { "drumIndex-step": { pitch: 60 } }
  const [rowViewModes, setRowViewModesState] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const stepStoreRef = useRef(null);
  if (!stepStoreRef.current) stepStoreRef.current = createDrumStepStore();
  const [isMuted, setIsMuted] = useState(false); // Drum sequencer mute state
  const [drumVolumes, setDrumVolumes] = useState({
    kick: 1.0,
    snare: 1.0,
    hihat: 1.0,
    clap: 1.0,
    perc: 1.0
  });
  const [drumCutMode, setDrumCutModeState] = useState({
    kick: true
  });

  const schedulerRef = useRef(null);
  const stepUiTimersRef = useRef(new Set());
  const nextStepTimeRef = useRef(0);
  const currentStepRef = useRef(0); // Use ref for scheduling logic
  const activeSources = useRef({}); // Track playing sources for manual triggers only
  const activePreviewSourcesRef = useRef({}); // Track keyboard-preview sources for key hold/release
  const bpmRef = useRef(bpm); // Always use latest BPM value
  const patternRef = useRef(pattern); // Always use latest pattern
  const patternLengthRef = useRef(16);
  const noteDataRef = useRef(noteData); // Always use latest note data
  const rowViewModesRef = useRef(rowViewModes);
  const drumSoundsRef = useRef(drumSounds);
  const customSamplesRef = useRef(customSamples); // Always use latest custom samples
  const drumVolumesRef = useRef(drumVolumes);
  const drumCutModeRef = useRef(drumCutMode);
  const isRecordingRef = useRef(false);
  const drumBuffersRef = useRef(drumBuffers);
  const pendingLoadsRef = useRef([]); // Queue for samples waiting for audio context
  const samplePrefetchCacheRef = useRef(new Map()); // Silent drag-time decoded buffers
  const sampleLoadRequestRef = useRef(new Map()); // Latest load wins independently per lane
  const drumMasterGainRef = useRef(null); // Master gain for drum sequencer
  const previewGainRef = useRef(null); // Unmuted preview bus for keyboard audition
  const drumGainNodesRef = useRef({}); // Individual gain nodes for each drum (for real-time volume)
  const drumAnalysersRef = useRef({}); // Individual analysers for each drum (for level metering)
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const historyLockRef = useRef(false);
  const lookAhead = 10.0; // How far ahead to schedule (ms) - reduced for better precision
  const scheduleAheadTime = 0.15; // How far ahead to schedule (seconds) - increased buffer
  const MAX_HISTORY_ENTRIES = 100;

  const normalizePatternLength = (length) => {
    const safe = Number.isFinite(length) ? Math.max(16, Math.floor(length)) : 16;
    return Math.ceil(safe / 16) * 16;
  };

  const ensureStepVisible = (stepIndex) => {
    if (!Number.isFinite(stepIndex) || stepIndex < 0) return patternLengthRef.current;
    if (stepIndex < patternLengthRef.current) return patternLengthRef.current;
    const next = normalizePatternLength(stepIndex + 1);
    setPatternLength(next);
    patternLengthRef.current = next;
    return next;
  };

  const cloneShallowMap = (value = {}) => (
    Object.entries(value || {}).reduce((acc, [key, entry]) => {
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        acc[key] = { ...entry };
      } else {
        acc[key] = entry;
      }
      return acc;
    }, {})
  );

  const createDrumSnapshot = () => ({
    pattern: { ...patternRef.current },
    noteData: cloneShallowMap(noteDataRef.current),
    rowViewModes: { ...rowViewModesRef.current },
    customSamples: cloneShallowMap(customSamplesRef.current),
    drumVolumes: { ...drumVolumesRef.current },
    drumCutMode: { ...drumCutModeRef.current },
    drumSounds: Array.isArray(drumSounds) ? drumSounds.map(sound => ({ ...sound })) : [],
    patternLength: patternLengthRef.current
  });

  const clearHistory = () => {
    undoStackRef.current = [];
    redoStackRef.current = [];
  };

  const pushUndoSnapshot = () => {
    if (historyLockRef.current) return;
    undoStackRef.current.push(createDrumSnapshot());
    if (undoStackRef.current.length > MAX_HISTORY_ENTRIES) {
      undoStackRef.current.shift();
    }
    redoStackRef.current = [];
  };

  const applyDrumSnapshot = (snapshot) => {
    if (!snapshot) return;

    historyLockRef.current = true;
    try {
      const nextPattern = snapshot.pattern || {};
      const nextNoteData = snapshot.noteData || {};
      const nextRowViewModes = snapshot.rowViewModes || {};
      const nextCustomSamples = snapshot.customSamples || {};
      const nextDrumVolumes = snapshot.drumVolumes || {};
      const nextDrumCutMode = snapshot.drumCutMode || {};
      const nextDrumSounds = normalizeDrumSounds(snapshot.drumSounds);
      const nextPatternLength = normalizePatternLength(snapshot.patternLength);

      setPattern(nextPattern);
      patternRef.current = nextPattern;
      setNoteDataState(nextNoteData);
      noteDataRef.current = nextNoteData;
      setRowViewModesState(nextRowViewModes);
      rowViewModesRef.current = nextRowViewModes;
      setCustomSamples(nextCustomSamples);
      customSamplesRef.current = nextCustomSamples;
      setDrumSounds(nextDrumSounds);
      setPatternLength(nextPatternLength);
      patternLengthRef.current = nextPatternLength;
      setDrumVolumes(nextDrumVolumes);
      drumVolumesRef.current = nextDrumVolumes;
      setDrumCutModeState(nextDrumCutMode);
      drumCutModeRef.current = nextDrumCutMode;

      if (registerDrumVolumes) {
        registerDrumVolumes(nextDrumVolumes);
      }

      Object.entries(nextDrumVolumes).forEach(([drumName, volume]) => {
        if (drumGainNodesRef.current[drumName]) {
          drumGainNodesRef.current[drumName].gain.value = volume;
        }
      });
    } finally {
      historyLockRef.current = false;
    }
  };

  const undo = () => {
    if (undoStackRef.current.length === 0) return false;
    const previous = undoStackRef.current.pop();
    redoStackRef.current.push(createDrumSnapshot());
    applyDrumSnapshot(previous);
    return true;
  };

  const redo = () => {
    if (redoStackRef.current.length === 0) return false;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push(createDrumSnapshot());
    applyDrumSnapshot(next);
    return true;
  };

  // Update BPM ref when BPM changes
  useEffect(() => {
    bpmRef.current = bpm;
    // console.log(`BPM updated to: ${bpm}`);
  }, [bpm]);

  useEffect(() => {
    isRecordingRef.current = Boolean(isRecording);
  }, [isRecording]);

  // Update pattern ref when pattern changes
  useEffect(() => {
    patternRef.current = pattern;
  }, [pattern]);

  useEffect(() => {
    patternLengthRef.current = patternLength;
  }, [patternLength]);

  // Update customSamples ref when customSamples changes
  useEffect(() => {
    customSamplesRef.current = customSamples;
    // console.log('🔄 CustomSamples updated:', Object.keys(customSamples));
  }, [customSamples]);

  useEffect(() => {
    drumVolumesRef.current = drumVolumes;
    if (registerDrumVolumes) {
      registerDrumVolumes(drumVolumes);
    }
  }, [drumVolumes, registerDrumVolumes]);

  useEffect(() => {
    drumCutModeRef.current = drumCutMode;
  }, [drumCutMode]);

  useEffect(() => {
    drumBuffersRef.current = drumBuffers;
    // Timeline pattern playback reads its own buffer map inside AudioContext,
    // so it has to be pushed there too. Doing it here — rather than leaving
    // each page to remember — matches how drumVolumes and trackFxLatency
    // already work, and is what keeps custom samples reaching the scheduler
    // the moment they finish decoding. A page that forgot this call got a
    // null map and silently scheduled nothing.
    if (registerDrumBuffers) {
      registerDrumBuffers(drumBuffers);
    }
  }, [drumBuffers, registerDrumBuffers]);

  // Update noteData ref when noteData changes
  useEffect(() => {
    noteDataRef.current = noteData;
  }, [noteData]);

  useEffect(() => {
    rowViewModesRef.current = rowViewModes;
  }, [rowViewModes]);

  useEffect(() => {
    drumSoundsRef.current = normalizeDrumSounds(drumSounds);
  }, [drumSounds]);

  const getLanePlaybackConfig = useCallback((drumName) => {
    const lane = normalizeDrumSounds(drumSoundsRef.current).find((item) => item.key === drumName) || {};
    const tuneSemitones = clampValue(lane.tuneSemitones, -24, 24, 0);
    const trimStartPct = clampValue(lane.trimStartPct, 0, 0.95, 0);
    const trimEndPct = clampValue(lane.trimEndPct, 0.05, 1, 1);
    const attackMs = clampValue(lane.attackMs, 0, 200, 2);
    const releaseMs = clampValue(lane.releaseMs, 1, 500, 10);
    return {
      tuneSemitones,
      trimStartPct: Math.min(trimStartPct, Math.max(0, trimEndPct - 0.01)),
      trimEndPct: Math.max(trimEndPct, Math.min(1, trimStartPct + 0.01)),
      attackMs,
      releaseMs
    };
  }, []);

  // Initialize note data for existing active steps (migration for old patterns)
  useEffect(() => {
    if (!pattern || Object.keys(pattern).length === 0) return;

    let needsUpdate = false;
    const newNoteData = { ...noteDataRef.current };

    // Check all active steps in the pattern
    Object.keys(pattern).forEach(key => {
      if (pattern[key] && !newNoteData[key]) {
        // Step is active but has no note data - add default C5
        newNoteData[key] = { pitch: 72 };
        needsUpdate = true;
        // console.log(`🔄 Migrating old step "${key}" to have default C5 note`);
      }
    });

    // Update state if we added any note data
    if (needsUpdate) {
      setNoteDataState(newNoteData);
      noteDataRef.current = newNoteData;
      // console.log('✅ Migration complete - all active steps now have note data');
    }
  }, [pattern]); // Run when pattern changes (including on load)

  // Load drum samples and create master gain
  useEffect(() => {
    if (!audioContext) return;

    // Create master gain node for drums — route through timeline master gain
    if (!drumMasterGainRef.current) {
      const destination = masterGainRef?.current ?? audioContext.destination;
      drumMasterGainRef.current = configureDrumBus(audioContext, destination).input;
      // console.log('✓ Drum master gain created');
    }

    const loadSamples = async () => {
      const buffers = {};

      // Use synthesized drums by default (no 404 errors)
      for (const name of DEFAULT_DRUM_SOUNDS.map(d => d.key)) {
        buffers[name] = createSynthDrum(audioContext, name);
      }

      setDrumBuffers(buffers);
      // console.log('✓ Synthesized drum samples loaded');
    };

    loadSamples();
    // masterGainRef is read above to wire the drum bus into the master chain.
    // It is a ref object from AudioContext, so its identity is stable and
    // listing it costs nothing; omitting it merely hid the dependency.
  }, [audioContext, masterGainRef]);

  useEffect(() => {
    if (!audioContext || !drumMasterGainRef.current) return;

    setDrumVolumes(prev => {
      const next = { ...prev };
      let changed = false;
      drumSounds.forEach(({ key }) => {
        if (typeof next[key] !== 'number') {
          next[key] = 1.0;
          changed = true;
        }
      });
      if (changed) {
        drumVolumesRef.current = next;
        return next;
      }
      return prev;
    });

    setDrumCutModeState((prev) => {
      const next = { ...prev };
      let changed = false;
      drumSounds.forEach(({ key }) => {
        if (typeof next[key] !== 'boolean') {
          const row = drumSounds.find((drum) => drum.key === key) || { key };
          next[key] = shouldChokeDrumLane(row, customSamplesRef.current[key], undefined);
          changed = true;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!drumSounds.some((drum) => drum.key === key)) {
          delete next[key];
          changed = true;
        }
      });
      if (changed) {
        drumCutModeRef.current = next;
        return next;
      }
      return prev;
    });

    drumSounds.forEach(({ key }) => {
      if (!drumGainNodesRef.current[key]) {
        const gainNode = audioContext.createGain();
        const volume = drumVolumesRef.current[key] ?? 1.0;
        gainNode.gain.value = volume;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        analyser.smoothingTimeConstant = 0;

        gainNode.connect(analyser);
        analyser.connect(drumMasterGainRef.current);

        drumGainNodesRef.current[key] = gainNode;
        drumAnalysersRef.current[key] = analyser;
      }

      if (!drumBuffersRef.current[key] && !customSamplesRef.current[key]?.loaded) {
        const fallback = createSynthDrum(audioContext, key);
        drumBuffersRef.current = { ...drumBuffersRef.current, [key]: fallback };
        setDrumBuffers(prev => ({ ...prev, [key]: fallback }));
      }
    });
  }, [audioContext, drumSounds, drumBuffers, masterGainRef]);

  // Create synthesized drum sounds as fallback
  const createSynthDrum = (ctx, drumName) => {
    const sampleRate = ctx.sampleRate;
    let duration, buffer, data;

    switch (drumName) {
      case 'kick':
        // Kick drum: Low frequency sweep with decay
        duration = 0.5;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const freq = 150 * Math.exp(-t * 15); // Frequency sweep from 150Hz down
          const env = Math.exp(-t * 8); // Amplitude decay
          data[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.8;
        }
        break;

      case 'snare':
        // Snare: White noise with tone and sharp decay
        duration = 0.2;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const noise = (Math.random() * 2 - 1) * 0.4;
          const tone = Math.sin(2 * Math.PI * 180 * t) * 0.3;
          const env = Math.exp(-t * 25);
          data[i] = (noise + tone) * env;
        }
        break;

      case 'hihat':
        // Hi-hat: Filtered noise with very short decay
        duration = 0.1;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const noise = (Math.random() * 2 - 1);
          const env = Math.exp(-t * 40);
          data[i] = noise * env * 0.3;
        }
        break;

      case 'clap':
        // Clap: Multiple noise bursts
        duration = 0.15;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const noise = (Math.random() * 2 - 1);
          const burst1 = t < 0.01 ? 1 : 0;
          const burst2 = (t > 0.02 && t < 0.03) ? 0.8 : 0;
          const burst3 = (t > 0.04 && t < 0.05) ? 0.6 : 0;
          const env = Math.exp(-t * 15);
          data[i] = noise * (burst1 + burst2 + burst3) * env * 0.5;
        }
        break;

      case 'perc':
        // Percussion: Mid-range tone with moderate decay
        duration = 0.3;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          const t = i / sampleRate;
          const freq = 400 * Math.exp(-t * 5);
          const env = Math.exp(-t * 10);
          data[i] = Math.sin(2 * Math.PI * freq * t) * env * 0.6;
        }
        break;

      default:
        // Generic beep
        duration = 0.1;
        buffer = ctx.createBuffer(1, sampleRate * duration, sampleRate);
        data = buffer.getChannelData(0);
        for (let i = 0; i < buffer.length; i++) {
          data[i] = Math.sin(2 * Math.PI * 440 * i / sampleRate) * 0.3;
        }
    }

    return buffer;
  };

  // Play a single drum sound
  const playDrum = (drumName, options = {}) => {
    if (!audioContext || !drumMasterGainRef.current) return false;
    const buffer = drumBuffersRef.current[drumName];
    if (!buffer) return false;

    try {
      const monitorPreview = options.monitor === true || isMuted;

      if (!drumGainNodesRef.current[drumName]) {
        const gainNode = audioContext.createGain();
        const volume = drumVolumesRef.current[drumName] ?? 1.0;
        gainNode.gain.value = volume;
        gainNode.connect(drumMasterGainRef.current);
        drumGainNodesRef.current[drumName] = gainNode;
      }

      if (monitorPreview && !previewGainRef.current) {
        previewGainRef.current = configureDrumBus(audioContext, audioContext.destination).input;
      }

      const lane = drumSoundsRef.current.find((row) => row.key === drumName) || { key: drumName };
      const explicitCut = Object.prototype.hasOwnProperty.call(drumCutModeRef.current, drumName)
        ? Boolean(drumCutModeRef.current[drumName])
        : undefined;
      const cutPrevious = shouldChokeDrumLane(
        lane, customSamplesRef.current[drumName], explicitCut
      );
      if (cutPrevious && activeSources.current[drumName]) {
        try {
          fadeAndStopDrumVoice(activeSources.current[drumName], audioContext.currentTime);
        } catch (e) {
          // Source may already be stopped
        }
      }

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      const laneConfig = getLanePlaybackConfig(drumName);
      const sourceStartOffset = buffer.duration * laneConfig.trimStartPct;
      const sourceTrimDuration = Math.max(0.01, (buffer.duration * laneConfig.trimEndPct) - sourceStartOffset);
      const tunedPlaybackRate = Math.max(0.25, Math.min(4.0, Math.pow(2, laneConfig.tuneSemitones / 12)));
      source.playbackRate.value = tunedPlaybackRate;
      const now = audioContext.currentTime;
      const laneVolume = drumVolumesRef.current[drumName] ?? 1.0;
      const targetGain = DRUM_VOICE_GAIN * (monitorPreview ? laneVolume : 1);
      const voiceGain = audioContext.createGain();
      const naturalDuration = sourceTrimDuration / Math.max(0.25, source.playbackRate.value || 1);
      const envelope = drumVoiceEnvelope({
        startTime: now,
        naturalDuration,
        attackMs: laneConfig.attackMs,
        releaseMs: laneConfig.releaseMs,
        playbackMode: 'drum'
      });
      applyDrumVoiceEnvelope(voiceGain.gain, targetGain, envelope);

      if (monitorPreview && previewGainRef.current) {
        // Song mode monitor: bypass muted drum master and preview to destination.
        const monitorGain = audioContext.createGain();
        monitorGain.gain.value = 1.0;
        source.connect(voiceGain);
        voiceGain.connect(monitorGain);
        monitorGain.connect(previewGainRef.current);
      } else {
        // Normal path: route through drum master.
        source.connect(voiceGain);
        voiceGain.connect(drumGainNodesRef.current[drumName]);
      }

      source.start(now, sourceStartOffset, sourceTrimDuration);
      source.stop(envelope.sourceStop);

      if (cutPrevious) {
        activeSources.current[drumName] = { source, gainNode: voiceGain, startTime: now };
        source.onended = () => {
          if (activeSources.current[drumName]?.source === source) {
            delete activeSources.current[drumName];
          }
        };
      }

      // Auto-cleanup on error
      source.onerror = (error) => {
        console.error(`Error playing ${drumName}:`, error);
      };
      return true;
    } catch (error) {
      console.error(`Failed to play ${drumName}:`, error);
      return false;
    }
  };

  // Play drum at specific time with optional pitch shifting
  const playDrumAtTime = (drumName, time, midiNote = null, durationSeconds = null, options = {}) => {
    if (!audioContext || !drumMasterGainRef.current) return false;
    const buffer = drumBuffersRef.current[drumName];
    if (!buffer) return false;

    try {
      if (!drumGainNodesRef.current[drumName]) {
        const gainNode = audioContext.createGain();
        const volume = drumVolumesRef.current[drumName] ?? 1.0;
        gainNode.gain.value = volume;
        gainNode.connect(drumMasterGainRef.current);
        drumGainNodesRef.current[drumName] = gainNode;
      }

      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      const voiceGain = audioContext.createGain();
      const laneConfig = getLanePlaybackConfig(drumName);
      const sourceStartOffset = buffer.duration * laneConfig.trimStartPct;
      const sourceTrimDuration = Math.max(0.01, (buffer.duration * laneConfig.trimEndPct) - sourceStartOffset);

      // Apply pitch shift if MIDI note is provided (FL Studio style)
      const midiSemitones = midiNote !== null ? (midiNote - 72) : 0;
      const totalSemitones = midiSemitones + laneConfig.tuneSemitones;
      let playbackRate = Math.pow(2, totalSemitones / 12);
      playbackRate = Math.max(0.25, Math.min(4.0, playbackRate));
      source.playbackRate.value = playbackRate;

      // Schedule the sound to play at the specified time
      const scheduleTime = Math.max(time, audioContext.currentTime);
      const velocity = Number.isFinite(options.velocity)
        ? Math.max(0, Math.min(1, options.velocity))
        : 1;
      const baseVolume = DRUM_VOICE_GAIN * velocity;
      const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
      const applyNoteLength = options.applyNoteLength === true;

      const naturalDuration = sourceTrimDuration / Math.max(0.25, source.playbackRate.value || 1);
      const envelope = drumVoiceEnvelope({
        startTime: scheduleTime,
        naturalDuration,
        noteDuration: hasDuration && applyNoteLength ? durationSeconds : null,
        attackMs: laneConfig.attackMs,
        releaseMs: laneConfig.releaseMs,
        playbackMode: hasDuration && applyNoteLength ? 'oneshot' : 'drum'
      });
      applyDrumVoiceEnvelope(voiceGain.gain, baseVolume, envelope);
      source.connect(voiceGain);
      voiceGain.connect(drumGainNodesRef.current[drumName]);

      const lane = drumSoundsRef.current.find((row) => row.key === drumName) || { key: drumName };
      const explicitCut = Object.prototype.hasOwnProperty.call(drumCutModeRef.current, drumName)
        ? Boolean(drumCutModeRef.current[drumName])
        : undefined;
      const shouldCutPrevious = options.cutPrevious
        ?? shouldChokeDrumLane(lane, customSamplesRef.current[drumName], explicitCut);
      if (shouldCutPrevious) {
        const prev = activeSources.current[drumName];
        if (prev) {
          fadeAndStopDrumVoice(prev, scheduleTime);
        }
      }

      try {
        source.start(scheduleTime, sourceStartOffset, sourceTrimDuration);
        source.stop(envelope.sourceStop);
      } catch (_) {
        return false;
      }

      // Auto-cleanup on error
      source.onerror = (error) => {
        console.error(`Error playing ${drumName}:`, error);
      };

      if (shouldCutPrevious) {
        activeSources.current[drumName] = { source, gainNode: voiceGain, startTime: scheduleTime };
        source.onended = () => {
          if (activeSources.current[drumName]?.source === source) {
            delete activeSources.current[drumName];
          }
        };
      }
      return true;
    } catch (error) {
      console.error(`Failed to play ${drumName} at time ${time}:`, error);
      return false;
    }
  };

  const previewDrumMidi = async (drumName, midiNote = 72) => {
    if (!audioContext) return false;
    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
      } catch (_) {
        // ignore
      }
    }

    if (!drumBuffersRef.current[drumName] && customSamplesRef.current?.[drumName]?.loaded) {
      await ensureCustomSamplesLoadedForPlayback();
    }

    if (!drumBuffersRef.current[drumName] && !customSamplesRef.current?.[drumName]?.loaded) {
      const synthBuffer = createSynthDrum(audioContext, drumName);
      drumBuffersRef.current = {
        ...drumBuffersRef.current,
        [drumName]: synthBuffer
      };
      setDrumBuffers(prev => ({
        ...prev,
        [drumName]: synthBuffer
      }));
    }

    if (!drumBuffersRef.current[drumName]) return false;
    return playDrumAtTime(drumName, audioContext.currentTime, midiNote);
  };

  // Live input routed to a drum lane plays through here. Registered upward
  // because InstrumentProvider wraps this one and cannot read it directly.
  const previewDrumMidiRef = useRef(previewDrumMidi);
  previewDrumMidiRef.current = previewDrumMidi;
  useEffect(() => {
    registerDrumTrigger?.((drumName, midiNote) => previewDrumMidiRef.current(drumName, midiNote));
    return () => registerDrumTrigger?.(null);
  }, [registerDrumTrigger]);

  const stopPreviewVoice = (voice, releaseSeconds = 0.01) => {
    if (!voice || !audioContext) return false;
    const now = audioContext.currentTime;

    try {
      if (voice.gainNode?.gain) {
        const current = voice.gainNode.gain.value;
        voice.gainNode.gain.cancelScheduledValues(now);
        voice.gainNode.gain.setValueAtTime(current, now);
        voice.gainNode.gain.linearRampToValueAtTime(0.0001, now + releaseSeconds);
      }
      voice.source?.stop(now + releaseSeconds + 0.002);
      return true;
    } catch (_) {
      try {
        voice.source?.stop();
      } catch (_) {
        // ignore
      }
      return false;
    }
  };

  const startPreviewDrumMidi = async (drumName, midiNote = 72, previewId = null) => {
    if (!audioContext) return false;

    if (audioContext.state === 'suspended') {
      try {
        await audioContext.resume();
      } catch (_) {
        // ignore
      }
    }

    if (!drumBuffersRef.current[drumName]) {
      const custom = customSamplesRef.current?.[drumName];
      if (custom?.url || custom?.sourceType === 'stock') {
        let url = custom.url || null;
        if (custom.sourceType === 'stock') {
          const token = sessionStorage.getItem('token');
          url = await assetResolver.resolve(custom, token);
        }
        if (url) {
          await loadCustomSampleFromUrl(drumName, custom.name || `${drumName}.wav`, url, {
            storageKey: custom.sourceType === 'stock' ? null : (custom.storageKey || null),
            sampleMeta: custom
          });
        }
      }
    }

    if (!drumBuffersRef.current[drumName] && !customSamplesRef.current?.[drumName]?.loaded) {
      const synthBuffer = createSynthDrum(audioContext, drumName);
      drumBuffersRef.current = {
        ...drumBuffersRef.current,
        [drumName]: synthBuffer
      };
      setDrumBuffers(prev => ({
        ...prev,
        [drumName]: synthBuffer
      }));
    }

    if (!drumBuffersRef.current[drumName]) return false;

    if (!previewGainRef.current) {
      const previewGain = audioContext.createGain();
      previewGain.gain.value = 1.0;
      previewGain.connect(audioContext.destination);
      previewGainRef.current = previewGain;
    }

    try {
      const source = audioContext.createBufferSource();
      source.buffer = drumBuffersRef.current[drumName];
      const laneConfig = getLanePlaybackConfig(drumName);
      const sourceStartOffset = source.buffer.duration * laneConfig.trimStartPct;
      const sourceTrimDuration = Math.max(0.01, (source.buffer.duration * laneConfig.trimEndPct) - sourceStartOffset);

      const semitones = (midiNote - 72) + laneConfig.tuneSemitones;
      let playbackRate = Math.pow(2, semitones / 12);
      playbackRate = Math.max(0.25, Math.min(4.0, playbackRate));
      source.playbackRate.value = playbackRate;

      const velocityGain = audioContext.createGain();
      const volume = drumVolumesRef.current[drumName] ?? 1.0;
      const now = audioContext.currentTime;
      const peakGain = DRUM_VOICE_GAIN * volume;
      const naturalDuration = sourceTrimDuration / Math.max(0.25, source.playbackRate.value || 1);
      const envelope = drumVoiceEnvelope({
        startTime: now,
        naturalDuration,
        attackMs: laneConfig.attackMs,
        releaseMs: laneConfig.releaseMs,
        playbackMode: 'drum'
      });
      applyDrumVoiceEnvelope(velocityGain.gain, peakGain, envelope);
      source.connect(velocityGain);
      velocityGain.connect(previewGainRef.current);
      source.start(now, sourceStartOffset, sourceTrimDuration);
      source.stop(envelope.sourceStop);

      if (previewId) {
        const previous = activePreviewSourcesRef.current[previewId];
        if (previous) {
          stopPreviewVoice(previous, 0.006);
        }
        activePreviewSourcesRef.current[previewId] = {
          source,
          gainNode: velocityGain
        };
      }

      source.onended = () => {
        if (previewId && activePreviewSourcesRef.current[previewId]?.source === source) {
          delete activePreviewSourcesRef.current[previewId];
        }
      };

      return true;
    } catch (error) {
      console.error(`Failed to preview ${drumName}:`, error);
      return false;
    }
  };

  const stopPreviewDrumMidi = (previewId = null) => {
    if (previewId) {
      const voice = activePreviewSourcesRef.current[previewId];
      if (!voice) return false;
      stopPreviewVoice(voice, 0.01);
      delete activePreviewSourcesRef.current[previewId];
      return true;
    }

    Object.values(activePreviewSourcesRef.current).forEach((voice) => {
      stopPreviewVoice(voice, 0.01);
    });
    activePreviewSourcesRef.current = {};
    return true;
  };

  // Get next step time based on BPM
  const getStepTime = () => {
    // 16 steps = 4 beats = 1 bar (like FL Studio)
    // Each step is a 16th note (4 steps per beat)
    const currentBpm = bpmRef.current;
    const beatsPerSecond = currentBpm / 60;
    const stepsPerSecond = beatsPerSecond * 4; // 4 steps per beat (16th notes)
    const stepTime = 1 / stepsPerSecond;

    // Debug logging
    if (currentStepRef.current === 0) {
      // console.log(`Step timing: ${currentBpm} BPM = ${stepTime.toFixed(4)}s per step, ${(stepTime * 16).toFixed(4)}s for 1 bar (16th notes)`);
    }

    return stepTime;
  };

  // Scheduler function
  const scheduler = () => {
    if (!audioContext) return;
    const startedAt = uiWorkStartedAt();

    while (nextStepTimeRef.current < audioContext.currentTime + scheduleAheadTime) {
      scheduleStep(currentStepRef.current, nextStepTimeRef.current);
      nextStep();
    }
    recordUiWork('standalone drum scheduler', startedAt);
  };

  async function ensureCustomSamplesLoadedForPlayback() {
    const currentCustomSamples = customSamplesRef.current || {};
    const missing = Object.entries(currentCustomSamples).filter(([drumName, sampleInfo]) =>
      (Boolean(sampleInfo?.url) || sampleInfo?.sourceType === 'stock') &&
      !drumBuffersRef.current[drumName]
    );

    if (missing.length === 0) return;

    const token = sessionStorage.getItem('token');
    await Promise.allSettled(
      missing.map(async ([drumName, sampleInfo]) => {
        let url = sampleInfo.url || null;
        if (sampleInfo.sourceType === 'stock') {
          url = await assetResolver.resolve(sampleInfo, token);
        }
        if (!url) return;
        await loadCustomSampleFromUrl(drumName, sampleInfo.name || `${drumName}.wav`, url, {
          storageKey: sampleInfo.sourceType === 'stock' ? null : (sampleInfo.storageKey || null),
          sampleMeta: sampleInfo
        });
      })
    );
  }

  // Schedule a single step
  const scheduleStep = (stepNumber, time) => {
    // Use the latest pattern and note data from refs
    const currentPattern = patternRef.current;
    const currentNoteData = noteDataRef.current;
    const currentDrumSounds = normalizeDrumSounds(drumSoundsRef.current);

    currentDrumSounds.forEach((drum, drumIdx) => {
      const key = `${drumIdx}-${stepNumber}`;
      if (currentPattern[key]) {
        // Check if this step has pitch data
        const noteInfo = currentNoteData[key];
        const pitches = Array.isArray(noteInfo?.pitches)
          ? noteInfo.pitches.filter((n) => Number.isFinite(Number(n))).map((n) => Number(n))
          : (Number.isFinite(noteInfo?.pitch) ? [Number(noteInfo.pitch)] : [null]);

        const durationSteps = Math.max(1, Number(noteInfo?.durationSteps || 1));
        const durationSeconds = durationSteps * getStepTime();
        const playbackMode = normalizePlaybackMode(drum?.playbackMode);

        // Play all pitches if chord data exists; fallback to single pitch/null.
        const stepVelocity = Number.isFinite(noteInfo?.velocity) ? noteInfo.velocity : 1;
        pitches.forEach((midiNote) => {
          playDrumAtTime(drum.key, time, midiNote, durationSeconds, {
            applyNoteLength: playbackMode === 'oneshot',
            velocity: stepVelocity
          });
        });
      }
    });

    // Update UI on the main thread
    const timer = setTimeout(() => {
      stepUiTimersRef.current.delete(timer);
      stepStoreRef.current.set(stepNumber);
    }, Math.max(0, (time - audioContext.currentTime) * 1000));
    stepUiTimersRef.current.add(timer);
  };

  // Move to next step
  const nextStep = () => {
    const stepTime = getStepTime();
    nextStepTimeRef.current += stepTime;

    const nextStepIndex = currentStepRef.current + 1;
    if (isRecordingRef.current) {
      // Recording mode should advance horizontally and never wrap.
      if (nextStepIndex >= patternLengthRef.current) {
        const nextLength = normalizePatternLength(nextStepIndex + 1);
        if (nextLength !== patternLengthRef.current) {
          patternLengthRef.current = nextLength;
          setPatternLength(nextLength);
        }
      }
      currentStepRef.current = nextStepIndex;
    } else {
      // Normal playback loops.
      currentStepRef.current = nextStepIndex % patternLengthRef.current;
    }

  };

  // Start playback
  const play = async () => {
    if (!audioContext) return false;
    if (isPlaying) return true;

    // Pattern mode can be started directly on mobile. Confirm the hardware
    // clock is running before anchoring the step scheduler to currentTime.
    if (!await activateAudioContext(audioContext)) return false;

    await ensureCustomSamplesLoadedForPlayback();

    setIsPlaying(true);
    stepStoreRef.current.set(0);
    currentStepRef.current = 0;
    nextStepTimeRef.current = audioContext.currentTime;

    schedulerRef.current = setSchedulerInterval(scheduler, lookAhead);
    scheduler();
    return true;
  };

  // Stop playback
  const stop = () => {
    if (schedulerRef.current) {
      clearSchedulerInterval(schedulerRef.current);
      schedulerRef.current = null;
    }

    stepUiTimersRef.current.forEach((timer) => clearTimeout(timer));
    stepUiTimersRef.current.clear();

    // Stop all active drum sources
    Object.values(activeSources.current).forEach((voice) => {
      stopPreviewVoice(voice, DRUM_CLICK_FADE_OUT);
    });
    activeSources.current = {};

    setIsPlaying(false);
    stepStoreRef.current.set(0);
    currentStepRef.current = 0;
  };

  // Toggle a step in the pattern
  const toggleStep = (drumIndex, stepIndex) => {
    pushUndoSnapshot();

    const key = `${drumIndex}-${stepIndex}`;
    const isCurrentlyActive = patternRef.current[key];

    const newPattern = {
      ...patternRef.current,
      [key]: !isCurrentlyActive
    };
    setPattern(newPattern);
    // Update ref synchronously so immediate saves work
    patternRef.current = newPattern;

    // Auto-add note data when step is activated (default to C5 = MIDI 72)
    const newNoteData = { ...noteDataRef.current };

    if (!isCurrentlyActive) {
      // Step is being turned ON - add default pitch if not already set
      if (!newNoteData[key]) {
        newNoteData[key] = { pitch: 72 }; // C5 (MIDI note 72)
        // console.log(`🎹 Auto-added C5 (MIDI 72) note for key "${key}" (drum ${drumIndex}, step ${stepIndex})`);
      }
    } else {
      // Step is being turned OFF - remove pitch data
      delete newNoteData[key];
      // console.log(`🎹 Removed note for key "${key}"`);
    }

    // console.log('📊 Current note data after toggle:', newNoteData);

    setNoteDataState(newNoteData);
    noteDataRef.current = newNoteData;
  };

  // Check if a step is active
  const isStepActive = (drumIndex, stepIndex) => {
    const key = `${drumIndex}-${stepIndex}`;
    return pattern[key] || false;
  };

  // Clear pattern
  const clearPattern = () => {
    if (Object.keys(patternRef.current).length === 0 && Object.keys(noteDataRef.current).length === 0) {
      return;
    }
    pushUndoSnapshot();
    setPattern({});
    // Update ref synchronously
    patternRef.current = {};
    setNoteDataState({});
    noteDataRef.current = {};
    setRowViewModesState({});
    rowViewModesRef.current = {};
    setPatternLength(16);
    patternLengthRef.current = 16;
  };

  // Load custom drum sample from file
  const loadCustomSample = async (drumName, file) => {
    if (!audioContext) {
      throw new Error('Audio context not initialized');
    }

    try {
      const requestKey = `lane:${drumName}`;
      const requestId = Symbol(requestKey);
      sampleLoadRequestRef.current.set(requestKey, requestId);
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await decodeAudioFile(audioContext, arrayBuffer, file.name);
      if (sampleLoadRequestRef.current.get(requestKey) !== requestId) return null;

      // The transport scheduler reads this ref synchronously. Update it before
      // React state so a live reschedule cannot capture the previous sample.
      drumBuffersRef.current = {
        ...drumBuffersRef.current,
        [drumName]: audioBuffer
      };
      setDrumBuffers(prev => ({
        ...prev,
        [drumName]: audioBuffer
      }));
      reschedulePatternsForDrumSampleChange?.();

      // Mark this drum as having a custom sample
      const localMeta = { name: file.name, loaded: true };
      customSamplesRef.current = { ...customSamplesRef.current, [drumName]: localMeta };
      setCustomSamples(prev => ({ ...prev, [drumName]: localMeta }));

      // console.log(`Loaded custom sample for ${drumName}: ${file.name}`);
      return true;
    } catch (error) {
      console.error(`Failed to load custom sample for ${drumName}:`, error);
      throw error;
    }
  };

  const samplePrefetchKey = (fileUrl, options = {}) => (
    options.cacheKey || options.storageKey || fileUrl
  );

  // Decode internal-browser samples as soon as dragging begins. This deliberately
  // changes no lane state; a cancelled drag is invisible and the small bounded
  // cache simply makes a later drop instant.
  const prefetchCustomSampleFromUrl = useCallback((fileUrl, options = {}) => {
    if (!audioContext || !fileUrl) return Promise.resolve(null);
    const key = samplePrefetchKey(fileUrl, options);
    const cached = samplePrefetchCacheRef.current.get(key);
    if (cached) return cached;

    const pending = fetch(fileUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`Sample prefetch failed (${response.status})`);
        return response.arrayBuffer();
      })
      .then((bytes) => decodeAudioFile(audioContext, bytes, fileUrl))
      .catch((error) => {
        samplePrefetchCacheRef.current.delete(key);
        throw error;
      });

    samplePrefetchCacheRef.current.set(key, pending);
    while (samplePrefetchCacheRef.current.size > 24) {
      samplePrefetchCacheRef.current.delete(samplePrefetchCacheRef.current.keys().next().value);
    }
    return pending;
  }, [audioContext]);

  // Load custom sample from URL (for sharing or library)
  const loadCustomSampleFromUrl = useCallback(async (drumName, fileName, fileUrl, options = {}) => {
    const existingSample = customSamplesRef.current[drumName];
    const requestedMeta = options.sampleMeta || {};
    const persistedSample = createPersistedDrumSample({
      existing: existingSample,
      name: requestedMeta.name || fileName,
      url: requestedMeta.sourceType === 'stock' ? null : (requestedMeta.url || fileUrl),
      storageKey: requestedMeta.storageKey || options.storageKey || null,
      sourceType: requestedMeta.sourceType || options.sourceType || null,
      assetId: requestedMeta.assetId || options.assetId || null,
      assetVersion: requestedMeta.assetVersion || options.assetVersion || 1
    });
    const identityKey = drumSampleBufferKey(persistedSample);
    const cachedBuffer = identityKey ? drumBuffersRef.current[identityKey] : null;
    if (cachedBuffer) {
      if (options.assignLane !== false) {
        // Selecting a pattern clip re-hydrates every sample it references, and
        // those buffers are usually already decoded. Only touch state for what
        // actually changed: rescheduling cancels every queued pattern voice, so
        // running it on a no-op re-hydration stutters the transport once per lane.
        const plan = planCachedDrumSampleAssignment({
          laneBuffer: drumBuffersRef.current[drumName],
          cachedBuffer,
          existingSample,
          nextSample: persistedSample
        });
        if (plan.bufferChanged) {
          drumBuffersRef.current = { ...drumBuffersRef.current, [drumName]: cachedBuffer };
          setDrumBuffers(prev => ({ ...prev, [drumName]: cachedBuffer }));
        }
        if (plan.metadataChanged) {
          customSamplesRef.current = { ...customSamplesRef.current, [drumName]: persistedSample };
          setCustomSamples(prev => ({ ...prev, [drumName]: persistedSample }));
        }
        if (plan.shouldReschedule) reschedulePatternsForDrumSampleChange?.();
      }
      return true;
    }

    if (!audioContext) {
      pendingLoadsRef.current.push({ drumName, fileName, fileUrl, options });
      return 'queued';
    }

    // Foreground drops compete per lane (the most recent drop wins). Background
    // hydration competes per sample identity so different pattern clips that use
    // the same lane can preload concurrently without cancelling one another.
    const requestKey = options.assignLane === false
      ? (identityKey || `url:${fileUrl}`)
      : `lane:${drumName}`;
    const requestId = Symbol(requestKey);
    sampleLoadRequestRef.current.set(requestKey, requestId);

    try {
      const commitDecodedBuffer = (audioBuffer) => {
        if (sampleLoadRequestRef.current.get(requestKey) !== requestId) return null;
        const nextBuffers = {
          ...drumBuffersRef.current,
          ...(identityKey ? { [identityKey]: audioBuffer } : {}),
          ...(options.assignLane === false ? {} : { [drumName]: audioBuffer })
        };
        drumBuffersRef.current = nextBuffers;
        setDrumBuffers(nextBuffers);
        if (options.assignLane !== false) {
          customSamplesRef.current = { ...customSamplesRef.current, [drumName]: persistedSample };
          setCustomSamples(prev => ({ ...prev, [drumName]: persistedSample }));
          reschedulePatternsForDrumSampleChange?.();
        }
        return true;
      };

      const prefetched = samplePrefetchCacheRef.current.get(samplePrefetchKey(fileUrl, options));
      if (prefetched) {
        try {
          const audioBuffer = await prefetched;
          return commitDecodedBuffer(audioBuffer);
        } catch (_) {
          samplePrefetchCacheRef.current.delete(samplePrefetchKey(fileUrl, options));
        }
      }

      // console.log(`📥 Loading custom sample for ${drumName} from: ${fileUrl}`);

      let finalUrl = fileUrl;
      const { storageKey } = options;
      let signedByKey = false;

      // Prefer signing by storageKey (stable, survives URL expiration)
      if (storageKey) {
        try {
          const token = sessionStorage.getItem('token');
          const signedByKeyResponse = await fetch(`${API_URL}/api/signed-url-by-key?key=${encodeURIComponent(storageKey)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (signedByKeyResponse.ok) {
            const { signedUrl } = await signedByKeyResponse.json();
            finalUrl = signedUrl;
            signedByKey = true;
            // console.log('Using signed URL by storage key for drum sample');
          }
        } catch (signedUrlError) {
          console.warn('Failed to get signed URL by key for drum sample, falling back to URL signing:', signedUrlError);
        }
      }

      // Check if URL is already signed (has X-Amz-Signature parameter)
      const isAlreadySigned = fileUrl.includes('X-Amz-Signature');
      const isS3Url = fileUrl.includes('s3.amazonaws.com') || fileUrl.includes('.s3.');

      // Only get a new signed URL if it's S3 and NOT already signed
      if (!signedByKey && isS3Url && !isAlreadySigned) {
        // console.log('S3 URL detected, getting signed URL...');
        try {
          const token = sessionStorage.getItem('token');
          const signedUrlResponse = await fetch(`${API_URL}/api/signed-url?url=${encodeURIComponent(fileUrl)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (signedUrlResponse.ok) {
            const { signedUrl } = await signedUrlResponse.json();
            finalUrl = signedUrl;
            // console.log('Using signed URL for S3 file');
          }
        } catch (signedUrlError) {
          console.warn('Failed to get signed URL, trying direct access:', signedUrlError);
        }
      } else if (isAlreadySigned) {
        // console.log('URL is already signed, using as-is');
      }

      let response;
      try {
        response = await fetch(finalUrl);
      } catch (fetchError) {
        console.warn(`⚠️ Network error loading custom sample for ${drumName}:`, fetchError.message);
        console.warn(`URL: ${finalUrl}`);

        // Attempt recovery: derive storage key from URL and retry signed-by-key
        const derivedKey = storageKey || extractStorageKeyFromUrl(fileUrl);
        if (derivedKey) {
          try {
            const token = sessionStorage.getItem('token');
            const signedByKeyResponse = await fetch(`${API_URL}/api/signed-url-by-key?key=${encodeURIComponent(derivedKey)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (signedByKeyResponse.ok) {
              const { signedUrl } = await signedByKeyResponse.json();
              response = await fetch(signedUrl);
            } else {
              console.warn(`⚠️ Could not load ${drumName} sample - skipping`);
              return false;
            }
          } catch (retryError) {
            console.warn(`⚠️ Retry failed for ${drumName}:`, retryError.message);
            return false;
          }
        } else {
          console.warn(`⚠️ Could not load ${drumName} sample - skipping`);
          return false;
        }
      }

      if (!response.ok) {
        console.warn(`⚠️ Failed to load custom sample for ${drumName}: ${response.status} ${response.statusText}`);
        console.warn(`URL: ${finalUrl}`);

        // Attempt recovery: derive storage key from URL and retry signed-by-key
        const derivedKey = storageKey || extractStorageKeyFromUrl(fileUrl);
        if (derivedKey) {
          try {
            const token = sessionStorage.getItem('token');
            const signedByKeyResponse = await fetch(`${API_URL}/api/signed-url-by-key?key=${encodeURIComponent(derivedKey)}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (signedByKeyResponse.ok) {
              const { signedUrl } = await signedByKeyResponse.json();
              response = await fetch(signedUrl);
              if (!response.ok) {
                console.warn(`⚠️ Retry failed for ${drumName}: ${response.status} ${response.statusText}`);
                return false;
              }
            } else {
              return false;
            }
          } catch (retryError) {
            console.warn('⚠️ Retry by key failed:', retryError);
            return false;
          }
        } else {
          if (response.status === 403) {
            console.warn(`This is likely an expired S3 URL. The sample will be skipped.`);
          }
          return false;
        }
      }

      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await decodeAudioFile(audioContext, arrayBuffer, fileName || finalUrl);

      return commitDecodedBuffer(audioBuffer);
    } catch (error) {
      console.warn(`⚠️ Skipping custom sample for ${drumName} due to error:`, error.message);
      // Don't throw - gracefully skip this sample and continue loading others
      return false;
    }
  }, [audioContext, reschedulePatternsForDrumSampleChange]);

  const hydratePersistedSample = useCallback(async (drumName, sampleInfo, assignLane = true) => {
    if (!drumName || !sampleInfo) return false;
    const token = sessionStorage.getItem('token');
    let url = sampleInfo.url || null;
    if (sampleInfo.sourceType === 'stock') {
      url = await assetResolver.resolve(sampleInfo, token);
    }
    if (!url) return false;
    return loadCustomSampleFromUrl(drumName, sampleInfo.name || `${drumName}.wav`, url, {
      storageKey: sampleInfo.sourceType === 'stock' ? null : (sampleInfo.storageKey || null),
      sampleMeta: sampleInfo,
      assignLane
    });
  }, [loadCustomSampleFromUrl]);

  // Process pending loads when audio context becomes available
  useEffect(() => {
    if (!audioContext || pendingLoadsRef.current.length === 0) return;

    const pending = [...pendingLoadsRef.current];
    pendingLoadsRef.current = [];

    pending.forEach(({ drumName, fileName, fileUrl, options }) => {
      loadCustomSampleFromUrl(drumName, fileName, fileUrl, options);
    });
  }, [audioContext, loadCustomSampleFromUrl]);

  // Check if drum has custom sample
  const hasCustomSample = (drumName) => {
    return customSamples[drumName]?.loaded || false;
  };

  // Get custom sample info
  const getCustomSampleInfo = (drumName) => {
    return customSamples[drumName] || null;
  };

  // Update custom sample metadata without re-downloading
  const setCustomSampleMeta = (drumName, meta) => {
    if (!drumName || !meta) return;

    const updatedMeta = createPersistedDrumSample({
      existing: customSamplesRef.current[drumName],
      ...meta
    });

    const identityKey = drumSampleBufferKey(updatedMeta);
    const activeBuffer = drumBuffersRef.current[drumName];
    if (identityKey && activeBuffer) {
      const nextBuffers = { ...drumBuffersRef.current, [identityKey]: activeBuffer };
      drumBuffersRef.current = nextBuffers;
      setDrumBuffers(nextBuffers);
    }

    setCustomSamples(prev => ({
      ...prev,
      [drumName]: updatedMeta
    }));

    customSamplesRef.current = {
      ...customSamplesRef.current,
      [drumName]: updatedMeta
    };
  };

  // Save pattern to session
  const savePatternToSession = async (sessionId, overrides = {}) => {
    if (!sessionId) return;

    try {
      const { keepalive = false, forceCanonical = false } = overrides;
      if (!forceCanonical) {
        return { success: false, skipped: true, reason: 'manual_save_required' };
      }
      const token = sessionStorage.getItem('token');

      // Use refs to get latest values (not stale closures)
      const currentPattern = overrides.pattern || patternRef.current;
      const currentNoteData = overrides.noteData || noteDataRef.current;
      const currentRowViewModes = overrides.rowViewModes || rowViewModesRef.current;
      const currentCustomSamples = overrides.customSamples || customSamplesRef.current;
      const currentDrumVolumes = overrides.drumVolumes || drumVolumesRef.current;
      const currentDrumCutMode = overrides.drumCutMode || drumCutModeRef.current;
      const currentDrumSounds = normalizeDrumSounds(overrides.drumSounds || drumSounds);
      const currentPatternLength = normalizePatternLength(overrides.patternLength || patternLengthRef.current);

      devLog('💾 Saving drum pattern to session:', {
            patternSteps: Object.keys(currentPattern).length,
            noteData: Object.keys(currentNoteData).length,
            customSamples: Object.keys(currentCustomSamples).length,
            drumVolumes: Object.keys(currentDrumVolumes || {}).length,
            drumCutMode: Object.keys(currentDrumCutMode || {}).length,
            drumSounds: currentDrumSounds.length,
            patternLength: currentPatternLength
          });

      const requestPayload = (expectedVersion) => ({
        sessionData: {
          pattern: currentPattern,
          noteData: currentNoteData,
          rowViewModes: currentRowViewModes,
          customSamples: currentCustomSamples,
          drumVolumes: currentDrumVolumes,
          drumCutMode: currentDrumCutMode,
          drumSounds: currentDrumSounds,
          patternLength: currentPatternLength
        },
        ...(Number.isInteger(expectedVersion) ? { expectedVersion } : {})
      });

      const sendPatch = (expectedVersion) => fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'PATCH',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload(expectedVersion))
      });

      const initialExpectedVersion = Number.isInteger(overrides.expectedVersion)
        ? overrides.expectedVersion
        : getSessionVersion(sessionId);

      let response = await sendPatch(initialExpectedVersion);
      let responseData = null;

      if (response.status === 409) {
        const conflictData = await response.json().catch(() => null);
        const currentVersion = conflictData?.currentVersion;
        if (Number.isInteger(currentVersion)) {
          setSessionVersion(sessionId, currentVersion);
          response = await sendPatch(currentVersion);
          if (!response.ok) {
            console.warn('⚠️ Drum save retry failed after rebasing to latest session version.');
            return {
              success: false,
              conflict: true,
              currentVersion,
              retryFailed: true,
              failed: true,
              status: response.status
            };
          }
        }
      }

      if (response.ok) {
        responseData = await response.json().catch(() => null);
        const nextVersion = responseData?.session?.version;
        if (Number.isInteger(nextVersion)) {
          setSessionVersion(sessionId, nextVersion);
        }
      } else {
        const errorData = await response.json().catch(() => null);
        return {
          success: false,
          failed: true,
          status: response.status,
          error: errorData?.error || `Drum save failed (${response.status})`
        };
      }
      return responseData;
    } catch (error) {
      console.error('Failed to save drum pattern:', error);
      return {
        success: false,
        failed: true,
        error: error?.message || 'Drum save failed'
      };
    }
  };

  const exportPatternSnapshot = () => ({
    pattern: patternRef.current || {},
    noteData: noteDataRef.current || {},
    rowViewModes: rowViewModesRef.current || {},
    customSamples: customSamplesRef.current || {},
    drumVolumes: drumVolumesRef.current || {},
    drumCutMode: drumCutModeRef.current || {},
    drumSounds: normalizeDrumSounds(drumSounds),
    patternLength: normalizePatternLength(patternLengthRef.current || 16)
  });

  // Load pattern from session
  const loadPatternFromSession = (sessionState) => {
    const sampleLoads = [];
    clearHistory();

    // console.log('📥 LOAD PATTERN FROM SESSION CALLED', {
    //   hasPattern: !!sessionState?.pattern,
    //   hasNoteData: !!sessionState?.noteData,
    //   hasCustomSamples: !!sessionState?.customSamples,
    //   customSamplesKeys: sessionState?.customSamples ? Object.keys(sessionState.customSamples) : []
    // });

    if (sessionState?.pattern) {
      const patternSteps = Object.keys(sessionState.pattern).length;
      devLog(`✓ Loading drum pattern: ${patternSteps} steps`, sessionState.pattern);
      setPattern(sessionState.pattern);
      patternRef.current = sessionState.pattern; // Update ref immediately
    } else {
      devWarn('⚠️ No pattern in sessionState!');
    }

    if (sessionState?.patternLength !== undefined) {
      const nextLength = normalizePatternLength(sessionState.patternLength);
      setPatternLength(nextLength);
      patternLengthRef.current = nextLength;
    } else if (sessionState?.pattern) {
      const usedSteps = Object.keys(sessionState.pattern)
        .filter((k) => sessionState.pattern[k])
        .map((k) => parseInt(String(k).split('-')[1], 10))
        .filter(Number.isFinite);
      if (usedSteps.length > 0) {
        const inferredLength = normalizePatternLength(Math.max(...usedSteps) + 1);
        setPatternLength(inferredLength);
        patternLengthRef.current = inferredLength;
      }
    }

    if (Array.isArray(sessionState?.drumSounds) && sessionState.drumSounds.length > 0) {
      setDrumSounds(normalizeDrumSounds(sessionState.drumSounds));
    }

    if (sessionState?.noteData) {
      const noteDataCount = Object.keys(sessionState.noteData).length;
      devLog(`✓ Loading note data: ${noteDataCount} notes`);
      setNoteDataState(sessionState.noteData);
      noteDataRef.current = sessionState.noteData; // Update ref immediately
    } else {
      devWarn('⚠️ No noteData in sessionState!');
    }

    if (sessionState?.rowViewModes && typeof sessionState.rowViewModes === 'object') {
      setRowViewModesState(sessionState.rowViewModes);
      rowViewModesRef.current = sessionState.rowViewModes;
    } else {
      setRowViewModesState({});
      rowViewModesRef.current = {};
    }

    if (sessionState?.customSamples) {
      setCustomSamples(sessionState.customSamples);
      customSamplesRef.current = sessionState.customSamples; // Update ref immediately
      // console.log('✓ Loaded custom samples from session:', Object.keys(sessionState.customSamples));

      // Load the actual audio buffers for custom samples
      Object.entries(sessionState.customSamples).forEach(([drumName, sampleInfo]) => {
        sampleLoads.push(hydratePersistedSample(drumName, sampleInfo, true));
      });
    } else {
      devWarn('⚠️ No custom samples in session data - samples may revert to default!');
    }

    // Each timeline pattern can own a different sample for the same drum lane.
    // Warm all identities without changing whichever pattern is currently open.
    (sessionState?.timelineClips || []).forEach((clip) => {
      if (clip?.clipType !== 'pattern') return;
      Object.entries(clip?.clipData?.customSamples || {}).forEach(([drumName, sampleInfo]) => {
        sampleLoads.push(hydratePersistedSample(drumName, sampleInfo, false));
      });
    });

    if (sessionState?.drumVolumes) {
      setDrumVolumes(sessionState.drumVolumes);
      drumVolumesRef.current = sessionState.drumVolumes;
      if (registerDrumVolumes) {
        registerDrumVolumes(sessionState.drumVolumes);
      }
      // Update gain nodes with loaded volumes
      Object.entries(sessionState.drumVolumes).forEach(([drumName, volume]) => {
        if (drumGainNodesRef.current[drumName]) {
          drumGainNodesRef.current[drumName].gain.value = volume;
        }
      });
      // console.log('✓ Loaded drum volumes from session');
    }

    if (sessionState?.drumCutMode && typeof sessionState.drumCutMode === 'object') {
      setDrumCutModeState(sessionState.drumCutMode);
      drumCutModeRef.current = sessionState.drumCutMode;
    } else {
      const sourceSounds = Array.isArray(sessionState?.drumSounds) && sessionState.drumSounds.length > 0
        ? sessionState.drumSounds
        : drumSounds;
      const defaultCutMode = sourceSounds.reduce((acc, drum) => {
        acc[drum.key] = shouldChokeDrumLane(drum, null, undefined);
        return acc;
      }, {});
      setDrumCutModeState(defaultCutMode);
      drumCutModeRef.current = defaultCutMode;
    }
    return Promise.all(sampleLoads).then(results => {
      if (isPreviewPlayer() && results.some(result => result === false)) {
        throw new Error('A drum sample could not be loaded. Refresh and retry.');
      }
      if (isPreviewPlayer()) {
        const missing = (sessionState?.timelineClips || []).filter(clip => clip.clipType === 'pattern')
          .some(clip => Object.keys(clip.clipData?.customSamples || {}).some(key =>
            !resolveDrumSampleBuffer(drumBuffersRef.current, clip.clipData, key)));
        if (missing) throw new Error('The assigned drum sample is not ready. Playback was stopped instead of using an old sound.');
      }
      // Publish the fully hydrated identity map before the caller starts playback.
      registerDrumBuffers?.(drumBuffersRef.current);
    });
  };

  // Load pattern from a timeline clip's data
  const loadPatternFromClipData = (clipData) => {
    if (!clipData) return;
    clearHistory();

    if (Array.isArray(clipData.noteEvents) && clipData.noteEvents.length > 0) {
      const ppq = Number(clipData.ppq) || 96;
      const ticksPerStep = ppq / 4;
      const derivedPattern = {};
      const derivedNoteData = {};
      let maxStep = 0;

      clipData.noteEvents.forEach((event) => {
        const drumIndex = Number(event?.drumIndex);
        if (!Number.isFinite(drumIndex)) return;
        const startTick = Number(event?.startTick) || 0;
        const durationTicks = Math.max(1, Number(event?.durationTicks) || ticksPerStep || 1);
        const startStep = Math.max(0, Math.floor(startTick / ticksPerStep));
        const durationSteps = Math.max(1, Math.ceil(durationTicks / ticksPerStep));
        const key = `${drumIndex}-${startStep}`;
        const pitches = Array.isArray(event?.pitches)
          ? event.pitches
          : (Number.isFinite(event?.pitch) ? [event.pitch] : []);
        const unique = [...new Set(pitches.map(Number).filter(Number.isFinite).sort((a, b) => a - b))];
        if (unique.length === 0) return;

        derivedPattern[key] = true;
        derivedNoteData[key] = {
          pitch: unique[0],
          ...(unique.length > 1 ? { pitches: unique } : {}),
          durationSteps,
          durationTicks,
          ppq
        };
        maxStep = Math.max(maxStep, startStep + durationSteps);
      });

      clipData = {
        ...clipData,
        pattern: derivedPattern,
        noteData: derivedNoteData,
        patternLength: normalizePatternLength(Math.max(clipData.patternLength || 16, maxStep || 16))
      };
    }

    if (clipData.patternLength !== undefined) {
      const nextLength = normalizePatternLength(clipData.patternLength);
      setPatternLength(nextLength);
      patternLengthRef.current = nextLength;
    } else if (clipData.pattern) {
      const usedSteps = Object.keys(clipData.pattern)
        .filter((k) => clipData.pattern[k])
        .map((k) => parseInt(String(k).split('-')[1], 10))
        .filter(Number.isFinite);
      if (usedSteps.length > 0) {
        const inferredLength = normalizePatternLength(Math.max(...usedSteps) + 1);
        setPatternLength(inferredLength);
        patternLengthRef.current = inferredLength;
      }
    }

    if (Array.isArray(clipData.drumSounds) && clipData.drumSounds.length > 0) {
      setDrumSounds(normalizeDrumSounds(clipData.drumSounds));
    }

    if (clipData.pattern) {
      setPattern(clipData.pattern);
      patternRef.current = clipData.pattern;
    }

    if (clipData.noteData) {
      setNoteDataState(clipData.noteData);
      noteDataRef.current = clipData.noteData;
    }

    if (clipData.rowViewModes && typeof clipData.rowViewModes === 'object') {
      setRowViewModesState(clipData.rowViewModes);
      rowViewModesRef.current = clipData.rowViewModes;
    } else {
      setRowViewModesState({});
      rowViewModesRef.current = {};
    }

    if (clipData.customSamples) {
      setCustomSamples(clipData.customSamples);
      customSamplesRef.current = clipData.customSamples;

      // Load buffers for custom samples
      Object.entries(clipData.customSamples).forEach(([drumName, sampleInfo]) => {
        hydratePersistedSample(drumName, sampleInfo, true);
      });
    }

    if (clipData.drumCutMode && typeof clipData.drumCutMode === 'object') {
      setDrumCutModeState(clipData.drumCutMode);
      drumCutModeRef.current = clipData.drumCutMode;
    } else {
      const sourceSounds = Array.isArray(clipData?.drumSounds) && clipData.drumSounds.length > 0
        ? clipData.drumSounds
        : drumSounds;
      const defaultCutMode = sourceSounds.reduce((acc, drum) => {
        acc[drum.key] = shouldChokeDrumLane(drum, null, undefined);
        return acc;
      }, {});
      setDrumCutModeState(defaultCutMode);
      drumCutModeRef.current = defaultCutMode;
    }
  };

  const extractStorageKeyFromUrl = (url) => storageKeyFromUrl(url);

  const repairCustomSamples = async (sessionId, sessionState) => {
    if (!sessionId || !sessionState?.customSamples) return sessionState;

    let didRepair = false;
    const repairedCustomSamples = { ...sessionState.customSamples };

    // Stock samples are resolved at playback time via AssetResolver — they
    // never have a url/storageKey and don't need URL repair.  Skip them.
    const repairable = Object.entries(repairedCustomSamples).filter(
      ([, sampleInfo]) => sampleInfo?.sourceType !== 'stock'
    );

    // If all custom samples are stock assets, skip the network fetch entirely.
    if (repairable.length === 0) {
      return { ...sessionState, customSamples: repairedCustomSamples };
    }

    repairable.forEach(([drumName, sampleInfo]) => {
      if (!sampleInfo?.storageKey && sampleInfo?.url) {
        const derivedKey = extractStorageKeyFromUrl(sampleInfo.url);
        if (derivedKey) {
          repairedCustomSamples[drumName] = {
            ...sampleInfo,
            storageKey: derivedKey
          };
          didRepair = true;
        }
      }
    });

    // Attempt recovery via session samples (source of truth).
    // Timeout after 5 s so a slow backend doesn't block session load indefinitely.
    try {
      const token = sessionStorage.getItem('token');
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      const response = await fetch(`${API_URL}/api/sessions/${sessionId}/samples`, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        const samples = data.samples || [];

        // Only repair user-uploaded samples, not stock assets
        repairable.forEach(([drumName, sampleInfo]) => {
          const match = samples.find(s => s.type === 'drum-sample' && s.drumName === drumName);
          if (match) {
            const derivedKey = match.storageKey || extractStorageKeyFromUrl(match.url);
            if (derivedKey && (sampleInfo.storageKey !== derivedKey || sampleInfo.url !== match.url)) {
              repairedCustomSamples[drumName] = {
                ...sampleInfo,
                name: match.filename || sampleInfo.name,
                url: match.url,
                storageKey: derivedKey
              };
              didRepair = true;
            }
          }
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        devWarn('⚠️ repairCustomSamples timed out — continuing with existing sample state');
      } else {
        devWarn('⚠️ Failed to fetch session samples for drum recovery:', error);
      }
    }

    if (didRepair) {
      devLog('🛠️ Repaired custom sample storage keys (not saving pattern - will save after load)');
      setCustomSamples(repairedCustomSamples);
      customSamplesRef.current = repairedCustomSamples;
      // DON'T save here - pattern hasn't been loaded yet! Will save after loadPatternFromSession
    }

    return {
      ...sessionState,
      customSamples: repairedCustomSamples
    };
  };

  // Set drums mute state (for remote updates)
  const setDrumsMute = (muted) => {
    if (drumMasterGainRef.current && isMuted !== muted) {
      setIsMuted(muted);
      drumMasterGainRef.current.gain.value = muted ? 0 : 1;
      // console.log(`🥁 Drums ${muted ? 'muted' : 'unmuted'} (remote)`);
    }
  };

  // Toggle drum sequencer mute (for local user)
  const toggleDrumsMute = () => {
    if (drumMasterGainRef.current) {
      const newMuteState = !isMuted;
      setIsMuted(newMuteState);
      drumMasterGainRef.current.gain.value = newMuteState ? 0 : 1;
      // console.log(`🥁 Drums ${newMuteState ? 'muted' : 'unmuted'} (local)`);
    }
  };

  // Set note data (pitch) for a specific step
  const setNoteData = (drumIndex, stepIndex, data) => {
    const key = `${drumIndex}-${stepIndex}`;
    const newNoteData = { ...noteDataRef.current };
    const existingValue = noteDataRef.current[key] || null;

    if (data === null) {
      delete newNoteData[key];
    } else {
      newNoteData[key] = data;
    }

    if (JSON.stringify(existingValue) === JSON.stringify(data)) {
      return;
    }
    pushUndoSnapshot();

    setNoteDataState(newNoteData);
    noteDataRef.current = newNoteData;
  };

  // Set full pattern and note data (for syncing with timeline clips)
  const setPatternState = (newPattern, newNoteData, newRowViewModes = rowViewModesRef.current) => {
    pushUndoSnapshot();
    setPattern(newPattern);
    patternRef.current = newPattern;
    setNoteDataState(newNoteData);
    noteDataRef.current = newNoteData;
    setRowViewModesState(newRowViewModes || {});
    rowViewModesRef.current = newRowViewModes || {};
  };

  const setRowViewMode = (drumIndex, mode = 'step', options = {}) => {
    const key = String(drumIndex);
    const normalizedMode = mode === 'piano' ? 'piano' : 'step';
    const current = rowViewModesRef.current[key] || 'step';
    if (current === normalizedMode) return;
    if (options.history !== false) {
      pushUndoSnapshot();
    }
    const next = { ...rowViewModesRef.current };
    if (normalizedMode === 'step') {
      delete next[key];
    } else {
      next[key] = normalizedMode;
    }
    setRowViewModesState(next);
    rowViewModesRef.current = next;
  };

  const resetRowViewModes = (options = {}) => {
    if (Object.keys(rowViewModesRef.current).length === 0) return;
    if (options.history !== false) {
      pushUndoSnapshot();
    }
    setRowViewModesState({});
    rowViewModesRef.current = {};
  };

  const setPatternLengthWithHistory = (length) => {
    const nextLength = normalizePatternLength(length);
    if (nextLength === patternLengthRef.current) return;
    pushUndoSnapshot();
    setPatternLength(nextLength);
    patternLengthRef.current = nextLength;
  };

  const setDrumVolume = (drumName, volume) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));

    // Update state
    setDrumVolumes(prev => ({
      ...prev,
      [drumName]: clampedVolume
    }));

    // Update ref for scheduling
    drumVolumesRef.current = {
      ...drumVolumesRef.current,
      [drumName]: clampedVolume
    };

    // Update gain node in real-time (affects currently playing and scheduled sounds)
    if (drumGainNodesRef.current[drumName]) {
      drumGainNodesRef.current[drumName].gain.value = clampedVolume;
    }
  };

  const setDrumCutMode = (drumName, enabled, options = {}) => {
    if (!drumName) return;
    const nextEnabled = Boolean(enabled);
    if (options.history !== false) {
      pushUndoSnapshot();
    }
    setDrumCutModeState((prev) => {
      const next = {
        ...prev,
        [drumName]: nextEnabled
      };
      drumCutModeRef.current = next;
      return next;
    });
  };

  const setDrumPlaybackMode = (drumName, mode, options = {}) => {
    if (!drumName) return null;
    const nextMode = normalizePlaybackMode(mode);
    if (options.history !== false) {
      pushUndoSnapshot();
    }
    let nextDrumSounds = null;
    setDrumSounds((prev) => {
      const normalized = normalizeDrumSounds(prev);
      nextDrumSounds = normalized.map((sound) => (
        sound.key === drumName ? { ...sound, playbackMode: nextMode } : sound
      ));
      return nextDrumSounds;
    });
    return nextDrumSounds;
  };

  const setDrumSoundPatch = (drumName, patch = {}, options = {}) => {
    if (!drumName || !patch || typeof patch !== 'object') return null;
    const sanitized = {};

    if (Number.isFinite(Number(patch.tuneSemitones))) {
      sanitized.tuneSemitones = clampValue(patch.tuneSemitones, -24, 24, 0);
    }
    if (Number.isFinite(Number(patch.trimStartPct))) {
      sanitized.trimStartPct = clampValue(patch.trimStartPct, 0, 0.95, 0);
    }
    if (Number.isFinite(Number(patch.trimEndPct))) {
      sanitized.trimEndPct = clampValue(patch.trimEndPct, 0.05, 1, 1);
    }
    if (Number.isFinite(Number(patch.attackMs))) {
      sanitized.attackMs = clampValue(patch.attackMs, 0, 200, 2);
    }
    if (Number.isFinite(Number(patch.releaseMs))) {
      sanitized.releaseMs = clampValue(patch.releaseMs, 1, 500, 10);
    }

    if (Object.prototype.hasOwnProperty.call(patch, 'playbackMode')) {
      sanitized.playbackMode = normalizePlaybackMode(patch.playbackMode);
    }

    if (Object.keys(sanitized).length === 0) return null;

    if (
      Number.isFinite(sanitized.trimStartPct)
      && Number.isFinite(sanitized.trimEndPct)
      && sanitized.trimStartPct >= sanitized.trimEndPct
    ) {
      sanitized.trimStartPct = Math.max(0, sanitized.trimEndPct - 0.01);
    } else if (Number.isFinite(sanitized.trimStartPct) && !Number.isFinite(sanitized.trimEndPct)) {
      sanitized.trimEndPct = Math.max(0.05, Math.min(1, sanitized.trimStartPct + 0.01));
    } else if (!Number.isFinite(sanitized.trimStartPct) && Number.isFinite(sanitized.trimEndPct)) {
      sanitized.trimStartPct = Math.max(0, sanitized.trimEndPct - 0.01);
    }

    if (options.history !== false) {
      pushUndoSnapshot();
    }

    let nextDrumSounds = null;
    setDrumSounds((prev) => {
      const normalized = normalizeDrumSounds(prev);
      nextDrumSounds = normalized.map((sound) => (
        sound.key === drumName ? { ...sound, ...sanitized } : sound
      ));
      return nextDrumSounds;
    });
    return nextDrumSounds;
  };

  const addDrumSound = (sound = {}) => {
    pushUndoSnapshot();

    const palette = ['#06B6D4', '#22C55E', '#F97316', '#A855F7', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];
    const normalizedName = (sound.name || '').trim();
    const name = normalizedName || `Drum ${drumSounds.length + 1}`;

    const baseKey = (sound.key || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `drum-${Date.now()}`).slice(0, 40);
    let key = baseKey;
    let suffix = 2;
    const existingKeys = new Set(drumSounds.map(d => d.key));
    while (existingKeys.has(key)) {
      key = `${baseKey}-${suffix++}`;
    }

    const created = {
      name,
      key,
      color: sound.color || palette[drumSounds.length % palette.length],
      playbackMode: normalizePlaybackMode(sound.playbackMode)
    };

    setDrumSounds(prev => [...prev, created]);
    setDrumVolumes(prev => {
      if (typeof prev[key] === 'number') return prev;
      const next = { ...prev, [key]: 1.0 };
      drumVolumesRef.current = next;
      return next;
    });
    setDrumCutModeState((prev) => {
      if (typeof prev[key] === 'boolean') return prev;
      const next = {
        ...prev,
        [key]: shouldChokeDrumLane(created, customSamplesRef.current[key], undefined)
      };
      drumCutModeRef.current = next;
      return next;
    });

    return created;
  };

  const renameDrumSound = (drumKey, nextName, options = {}) => {
    const trimmedName = String(nextName || '').trim();
    if (!drumKey || !trimmedName) return null;

    if (options.history !== false) {
      pushUndoSnapshot();
    }

    const currentDrumSounds = normalizeDrumSounds(drumSounds);
    const updatedDrumSounds = currentDrumSounds.map((sound) => (
      sound.key === drumKey ? { ...sound, name: trimmedName } : sound
    ));

    const changed = updatedDrumSounds.some((sound, idx) => sound.name !== currentDrumSounds[idx]?.name);
    if (!changed) {
      return currentDrumSounds;
    }

    setDrumSounds(updatedDrumSounds);
    return updatedDrumSounds;
  };

  // Get note data for a specific step
  const getNoteData = (drumIndex, stepIndex) => {
    const key = `${drumIndex}-${stepIndex}`;
    return noteDataRef.current[key] || null;
  };

  // Clear all notes for a specific drum
  const clearDrumNotes = (drumIndex) => {
    const hasNotes = Object.keys(noteDataRef.current).some((key) => String(key).startsWith(`${drumIndex}-`));
    const hasPatternSteps = Object.keys(patternRef.current).some((key) => String(key).startsWith(`${drumIndex}-`));
    if (!hasNotes && !hasPatternSteps) {
      return;
    }
    pushUndoSnapshot();

    const newNoteData = { ...noteDataRef.current };
    const newPattern = { ...patternRef.current };

    // Remove all notes and pattern entries for this drum
    for (let step = 0; step < patternLengthRef.current; step++) {
      const key = `${drumIndex}-${step}`;
      delete newNoteData[key];
      delete newPattern[key];
    }

    setNoteDataState(newNoteData);
    noteDataRef.current = newNoteData;
    setPattern(newPattern);
    patternRef.current = newPattern;
  };

  // Clear all drum session state (for when leaving/switching sessions)
  const clearSession = () => {
    clearHistory();

    // Stop playback
    stop();

    // Clear pattern and notes
    setPattern({});
    patternRef.current = {};
    setNoteDataState({});
    noteDataRef.current = {};
    setRowViewModesState({});
    rowViewModesRef.current = {};

    // Clear custom samples
    setCustomSamples({});
    customSamplesRef.current = {};

    // Reset drum lanes to defaults
    setDrumSounds(normalizeDrumSounds(DEFAULT_DRUM_SOUNDS));
    setPatternLength(16);
    patternLengthRef.current = 16;

    // Reset volumes to default
    const defaultVolumes = DEFAULT_DRUM_SOUNDS.reduce((acc, drum) => {
      acc[drum.key] = 1.0;
      return acc;
    }, {});
    setDrumVolumes(defaultVolumes);
    drumVolumesRef.current = defaultVolumes;
    const defaultCutMode = DEFAULT_DRUM_SOUNDS.reduce((acc, drum) => {
      acc[drum.key] = shouldChokeDrumLane(drum, null, undefined);
      return acc;
    }, {});
    setDrumCutModeState(defaultCutMode);
    drumCutModeRef.current = defaultCutMode;

    // Update gain nodes to default volumes
    Object.entries(defaultVolumes).forEach(([drumName, volume]) => {
      if (drumGainNodesRef.current[drumName]) {
        drumGainNodesRef.current[drumName].gain.value = volume;
      }
    });

    // Reset to step 0
    stepStoreRef.current.set(0);
    currentStepRef.current = 0;
  };

  const value = {
    drumBuffers,
    drumSounds: normalizeDrumSounds(drumSounds),
    pattern,
    patternLength,
    noteData,
    rowViewModes,
    isPlaying,
    customSamples,
    isMuted,
    drumVolumes,
    drumCutMode,
    drumAnalysersRef,
    playDrum,
    previewDrumMidi,
    startPreviewDrumMidi,
    stopPreviewDrumMidi,
    play,
    stop,
    toggleStep,
    isStepActive,
    clearPattern,
    setPattern,
    setPatternLength: setPatternLengthWithHistory,
    ensureStepVisible,
    setPatternState,
    setRowViewMode,
    resetRowViewModes,
    setNoteData,
    getNoteData,
    clearDrumNotes,
    loadCustomSample,
    loadCustomSampleFromUrl,
    prefetchCustomSampleFromUrl,
    setCustomSampleMeta,
    hasCustomSample,
    getCustomSampleInfo,
    setDrumVolume,
    setDrumCutMode,
    setDrumPlaybackMode,
    setDrumSoundPatch,
    addDrumSound,
    renameDrumSound,
    undo,
    redo,
    savePatternToSession,
    exportPatternSnapshot,
    loadPatternFromSession,
    loadPatternFromClipData,
    // The AI path assigns a sample by reference and needs the audio fetched.
    hydratePersistedSample,
    repairCustomSamples,
    toggleDrumsMute,
    setDrumsMute,
    clearSession
  };

  return (
    <DrumStepContext.Provider value={stepStoreRef.current}>
      <DrumContext.Provider value={value}>{children}</DrumContext.Provider>
    </DrumStepContext.Provider>
  );
};
